import React from 'react';
import type { Amount, LiquidityAmount, TokenAmount } from '@akropolis-web/primitives';

import { styled } from 'core/styles';

import { Decimal } from './Decimal';
import type { FormattedAmountProps } from './types';

type Props<A extends LiquidityAmount | TokenAmount> = Pick<
  FormattedAmountProps<A>,
  | 'withSI'
  | 'precision'
  | 'symbolVariant'
  | 'symbolSize'
  | 'hasSign'
  | 'wrap'
  | 'siPrecision'
  | 'renderSymbol'
> & {
  sum: A;
};

export function LiquidityOrTokenAmountComponent<A extends LiquidityAmount | TokenAmount>(
  props: Props<A>,
) {
  const {
    sum,
    withSI,
    precision,
    siPrecision,
    symbolVariant = 'text',
    symbolSize = 'inherit',
    hasSign,
    wrap = 'nowrap',
    renderSymbol,
  } = props;
  const { detailed } = sum.toFormattedBalance(
    precision,
    symbolVariant !== 'none',
    withSI,
    siPrecision,
  );

  const hideStartSymbolAndSpace = symbolVariant === 'none' && !renderSymbol;
  const hideEndSymbolAndSpace = symbolVariant === 'none' && !renderSymbol;

  const sign = hasSign && !sum.isZero() ? detailed.negativeSign || '+' : detailed.negativeSign;
  const startSymbol = hideStartSymbolAndSpace ? false : detailed.startSymbol;
  const startSpace = hideStartSymbolAndSpace
    ? false
    : (detailed.startSpace && wrap === 'wrap' && ' ') || detailed.startSpace;
  const { siPower } = detailed;

  const value = (
    <Decimal decimal={{ fractional: detailed.fractional, integer: detailed.integer }} />
  );

  const endSpace = hideEndSymbolAndSpace
    ? false
    : (detailed.endSpace && wrap === 'wrap' && ' ') || detailed.endSpace;
  const endSymbol = hideEndSymbolAndSpace ? false : detailed.endSymbol;

  return (
    <>
      {sign}
      {startSymbol && renderSymbol ? (
        renderSymbol(sum)
      ) : (
        <Symbol size={symbolSize}>{startSymbol}</Symbol>
      )}
      {startSpace}
      {value}
      {siPower}
      {endSpace}
      {endSymbol && renderSymbol ? (
        renderSymbol(sum)
      ) : (
        <Symbol size={symbolSize}>{endSymbol}</Symbol>
      )}
    </>
  );
}

const Symbol = styled('span', { shouldForwardProp: prop => prop !== 'size' })<{
  size: FormattedAmountProps<Amount>['symbolSize'];
}>(({ size }) => ({
  fontSize: (size === 'small' && '0.5em') || (size === 'medium' && '0.7em') || undefined,
}));
